// ASCII specimen art (DESIGN_SPEC §10.14). Brand surfaces only; real screens are PNGs.
//   <Ascii art maxPx? scan? />   aria-hidden <pre>; glyph size min(maxPx, container-derived) against the enclosing .specimen (cqi)
//       art: { cols, rows, lines, accent: [row, c0, c1][] } (src/assets/specimen/moth-*.json); accent runs render as .pin
//       maxPx: 4.2 for the 240-col moth (default), 4.6 for the 72-col empty-state moth
//       scan: "once" = one 1.8 s pass on first view · 0..1 = follow hashing progress · "done" = fade out (hidden under reduced motion)
import { memo, useMemo, type ReactNode } from "react";
import { cx, vars } from "./cx";

export type Art = { cols: number; rows: number; lines: readonly string[]; accent: readonly (readonly number[])[] };

function glyphs(art: Art): ReactNode[] {
  return art.lines.map((line, r) => {
    const runs = art.accent.filter((a) => a[0] === r);
    if (!runs.length) return line + "\n";
    const out: ReactNode[] = [];
    let pos = 0;
    for (const [, c0, c1] of runs) {
      out.push(
        line.slice(pos, c0),
        <span key={c0} className="pin">
          {line.slice(c0, c1)}
        </span>,
      );
      pos = c1;
    }
    out.push(line.slice(pos) + "\n");
    return <span key={r}>{out}</span>;
  });
}

const Pre = memo(function Pre({ art, maxPx }: { art: Art; maxPx: number }) {
  const content = useMemo(() => glyphs(art), [art]);
  return (
    <pre className="ascii" aria-hidden="true" style={vars({ "--cols": art.cols, "--afs-max": `${maxPx}px` })}>
      {content}
    </pre>
  );
});

export function Ascii({ art, maxPx = 4.2, scan }: { art: Art; maxPx?: number; scan?: "once" | "done" | number }) {
  if (scan === undefined) return <Pre art={art} maxPx={maxPx} />;
  return (
    <div className="ascii-wrap">
      <Pre art={art} maxPx={maxPx} />
      <span
        className={cx("scan", scan === "once" && "scan--once", scan === "done" && "scan--done")}
        style={typeof scan === "number" ? vars({ "--p": Math.max(0, Math.min(1, scan)) }) : undefined}
        aria-hidden="true"
      />
    </div>
  );
}
